import {
  Component,
  type Context,
  type ReactElement,
  type ReactNode
} from 'react';
import {
  ConfigurationContext,
  type ConfigurationContextState
} from '../../../contexts/config.context';
import { logError } from '../../../helpers/logger.helper';
import { markdownCodeBlock } from '../../../helpers/markdown-preprocessing.helper';
import { getTenantName } from '../../../helpers/next.helpers';
import { PageContent } from '../../layout/page-content.component';
import { Error500 } from '../error-500/error-500.component';

export type ErrorBoundaryProps = {
  children: ReactElement;
};
export type ErrorBoundaryState = {
  hasError: boolean;
  error?: { message: string; stack?: string };
};

export class PageErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: { message: null, stack: null } };
  }

  static contextType?: Context<ConfigurationContextState> =
    ConfigurationContext;
  context: ConfigurationContextState;

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }
  componentDidCatch(
    error: Error | { name: string; message: string },
    errorInfo: never
  ): void {
    // You can also log the error to an error reporting service
    this.setState({
      error: typeof error === 'string' ? { message: error, stack: '' } : error
    });
    logError(error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <PageContent isLoading={false} title="Page error">
          <Error500
            subject={
              this.state.error
                ? `[🐛][${getTenantName()}] - ${this.state.error.message}`
                : `[${getTenantName()}] 500 Error - Technical Issue`
            }
            body={
              (this.state.error?.stack
                ? markdownCodeBlock(this.state.error.stack)
                : `I encountered a 500 error on: ${typeof window !== 'undefined' ? window.location.href : ''}`) +
              '\n\n' +
              (typeof window !== 'undefined' ? window.location.href : '')
            }
            errorDetails={this.state.error?.stack}
          />
        </PageContent>
      );
    }

    return this.props.children;
  }
}
